import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { useAuth } from "hooks/useAuth";
import {
  CHAMPION_VIDEO_SLOTS,
  listChampionVideoRenderJobs,
  requestChampionVideoRender,
  resolveVideoStatuses,
} from "services/fde/championVideoRolloutService";

const inlineLoading = (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

const VideoLibraryPage = () => {
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId || userData?.institutionId || organizationId || null;

  const profileReady = userData !== undefined;
  const canAccess = isGlobalAdmin || userData?.role === "admin";

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [requestingId, setRequestingId] = useState("");

  const statuses = useMemo(() => resolveVideoStatuses(jobs), [jobs]);

  const reload = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await listChampionVideoRenderJobs(institutionId);
      setJobs(rows);
    } catch (err) {
      setLoadError(true);
      setErrorMessage(err?.message || "Failed to load video library.");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    if (!profileReady || !canAccess || !institutionId) {
      setJobs([]);
      setLoading(false);
      return undefined;
    }
    reload();
    return undefined;
  }, [profileReady, canAccess, institutionId, reload]);

  const handleRequest = async (videoId) => {
    setRequestingId(videoId);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await requestChampionVideoRender({ institutionId, videoId });
      setSuccessMessage(
        "Requested — a rendered video will be available soon.",
      );
      await reload();
    } catch (err) {
      setErrorMessage(
        err?.message || err?.details || "Could not request rollout video.",
      );
    } finally {
      setRequestingId("");
    }
  };

  if (!profileReady) return inlineLoading;

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Institution admin access is required.</Alert>
      </Box>
    );
  }

  if (!institutionId) {
    return (
      <Stack spacing={3}>
        <DashboardPageHeader
          title="Video Library"
          subtitle="Champion rollout videos"
        />
        <Alert severity="info">
          No institution is linked to your account yet.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="Video Library"
        subtitle="Champion rollout videos for your institution"
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && !loadError && (
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {loadError && (
        <DataLoadErrorAlert
          onRetry={reload}
          message={errorMessage || undefined}
        />
      )}

      {loading ? (
        inlineLoading
      ) : (
        <Stack spacing={2}>
          {CHAMPION_VIDEO_SLOTS.map(({ videoId, label }) => {
            const slot = statuses[videoId] || { status: "none", job: null };
            const status = slot.status;
            return (
              <Box
                key={videoId}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1">{label}</Typography>
                    {status === "none" && (
                      <Typography variant="body2" color="text.secondary">
                        Not yet requested
                      </Typography>
                    )}
                    {status === "pending" && (
                      <Typography variant="body2" color="text.secondary">
                        Requested — a rendered video will be available soon
                      </Typography>
                    )}
                    {status === "complete" && (
                      <Typography variant="body2" color="text.secondary">
                        Rendered and ready to distribute
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {status === "none" && (
                      <LoadingButton
                        variant="contained"
                        loading={requestingId === videoId}
                        onClick={() => handleRequest(videoId)}
                      >
                        Request rollout video
                      </LoadingButton>
                    )}
                    {status === "pending" && (
                      <Button disabled variant="outlined">
                        Requested
                      </Button>
                    )}
                    {status === "complete" && slot.job?.outputUrl && (
                      <>
                        <Button
                          component="a"
                          href={slot.job.outputUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                        >
                          Preview
                        </Button>
                        <Button
                          component={RouterLink}
                          to="/dashboard/institution-rollout/distribute"
                          variant="contained"
                        >
                          Distribute
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default VideoLibraryPage;
